import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useHomepagePageData } from 'src/app/pages/homepageV2/pageContext';

import type { TopStory } from '../../types';

type Result = ModelHookResult<
	{
		topStories: TopStory[];
	},
	{
		trackStoryClick: (url: string) => void;
	}
>;

export function useTopImpactStories(): Result {
	const track = useTracking();
	const {
		data: { v3TopImpactStories },
	} = useHomepagePageData();

	const { translate, translatePlural } = useI18n();

	const topStories = v3TopImpactStories.map((story, index) => {
		const labelTitle =
			story.badgeTitle ??
			(story.petition.isVictory
				? translate('corgi.homepage.top_impact_stories.badge.label_title_victory')
				: translate('corgi.homepage.top_impact_stories.badge.label_title_trending'));

		const labelText = translatePlural(
			'corgi.homepage.top_impact_stories.badge.label_text',
			Number(story.petition.signatureCount),
			{
				signatureCount: story.petition.signatureCount,
			},
		);

		return {
			key: index + 1,
			image: story.imageUrl,
			altText: translate('corgi.homepage.top_impact_stories.avatar.alt_text', { index: index + 1, totalStories: 5 }),
			ariaLabel: [
				translate('corgi.homepage.top_impact_stories.aria_label', { index: index + 1, totalStories: 5 }),
				`${labelTitle} ${labelText}`,
				story.summary,
			].join('. '),
			url: story.testimonialUrl || `/p/${story.petition.slug}`,
			summary: story.summary,
			labelTitle,
			labelText,
			dataTestIdBase: `top-impact-story-${index + 1}-of-5`,
		};
	});

	return {
		data: {
			topStories,
		},
		actions: {
			trackStoryClick: (url: string) => {
				void track('homepage_top_impact_story_click', { url });
			},
		},
	};
}
